import { TaskColumn } from "@common/types/views/task-view-enums";
import type { BoardCardFieldPlacement } from "@design-system/Board/Board.types";
import { TASK_CARD_COMMENTS_FIELD } from "@components/tasks/board/taskCardColumns";
import type { TaskCardFieldId } from "./taskBoardView";

/** Card field placement by card field id. */
export type TaskCardFieldPlacements = Readonly<
    Record<string, BoardCardFieldPlacement>
>;

/**
 * Every optional card field with the real card's placement: the entities'
 * context line on its own row, the rest as tags. Keyed by `TaskCardFieldId`,
 * so a field missing here fails to compile.
 */
export const DEFAULT_TASK_CARD_FIELD_PLACEMENTS: Readonly<
    Record<TaskCardFieldId, BoardCardFieldPlacement>
> = {
    [TaskColumn.DUE_DATE]: "inline",
    [TaskColumn.PRIORITY]: "inline",
    [TaskColumn.ENTITIES]: "stacked",
    [TaskColumn.CLOSE_STAGE]: "inline",
    [TaskColumn.CLOSE]: "inline",
    [TaskColumn.SERVICE_PERIOD]: "inline",
    [TaskColumn.VISIBILITY]: "inline",
    [TaskColumn.ESTIMATE]: "inline",
    [TaskColumn.GROUP]: "inline",
    [TaskColumn.CREATED_AT]: "inline",
    [TaskColumn.CREATED_BY]: "inline",
    [TaskColumn.COMPLETED_BY]: "inline",
    [TASK_CARD_COMMENTS_FIELD]: "inline",
};

export const CARD_FIELD_PLACEMENT_OPTIONS: ReadonlyArray<{
    value: BoardCardFieldPlacement;
    label: string;
}> = [
    { value: "topEnd", label: "Top right" },
    { value: "stacked", label: "Own row" },
    { value: "inline", label: "Grouped" },
    { value: "bottomEnd", label: "Bottom right" },
];

/** Narrows a picked option value back to its placement. */
export const toCardFieldPlacement = (
    value: string,
): BoardCardFieldPlacement | undefined =>
    CARD_FIELD_PLACEMENT_OPTIONS.find((option) => option.value === value)
        ?.value;

/** A field's placement: the Controls panel's pick, else the real card's. */
export const getTaskCardFieldPlacement = (
    placements: TaskCardFieldPlacements,
    id: TaskCardFieldId,
) => placements[id] ?? DEFAULT_TASK_CARD_FIELD_PLACEMENTS[id];
